import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useParams, useNavigate, Link } from "react-router-dom";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* ================= helpers (LOCAL dates, not ISO!) ================= */
const pad2 = (n) => String(n).padStart(2, "0");
const ymdLocal = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const addDaysLocal = (yyyy_mm_dd, n) => {
  if (!yyyy_mm_dd) return "";
  const d = new Date(yyyy_mm_dd + "T00:00:00");
  d.setDate(d.getDate() + n);
  return ymdLocal(d); // keep LOCAL Y-M-D (no toISOString())
};

const formatDMY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const to24h = (h12, ampm) =>
  ampm === "AM" ? Number(h12) % 12 : (Number(h12) % 12) + 12;
const decFromHM = (h24, m) => +(h24 + m / 60).toFixed(2);

const minutesFromDateHM = (dateStr, h24, m) => {
  const y = Number(dateStr.slice(0, 4));
  const mo = Number(dateStr.slice(5, 7));
  const d = Number(dateStr.slice(8, 10));
  const dayOrd = y * 372 + mo * 31 + d; // simple monotonic ordinal
  return dayOrd * 1440 + h24 * 60 + m;
};
const decToHM = (dec) => {
  const n = Number(dec || 0);
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  return [Math.max(0, Math.min(23, h)), Math.max(0, Math.min(59, m))];
};
const h24To12 = (h24) => {
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return { h12: String(h12), ampm };
};
// Calendar day span (fallback) from stored decimals + wohrs
const daySpanFromStored = (st_dec, end_dec, wohrs) => {
  const s = Number(st_dec || 0);
  const e = Number(end_dec || 0);
  const w = Number(wohrs || 0);
  const base = e - s >= 0 ? e - s : 24 - s + e; // min same/overnight
  const overnight = e < s ? 1 : 0;
  const extra = Math.max(0, Math.floor((w - base + 1e-9) / 24));
  return overnight + extra;
};

const VehiclePositionEdit = () => {
  useAuthRedirect();
  const { id, vcode, branch, vehno, vehname } = useParams();
  const navigate = useNavigate();
  const canEdit = hasPermission("Vehicle Daily Progress Entry", "edit");

  const [locations, setLocations] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [formData, setFormData] = useState({
    fld_drivername: "",
    fld_location: "",
    fld_sthrs: "",
    fld_endhrs: "",
    fld_wohrs: "",
    fld_stkm: "",
    fld_endkm: "",
    fld_vtype: "",
    fld_vbasis: "",
    fld_vdate: "",
    fld_load_date: "",
    fld_unload_date: "",
    fld_dieselqty: "",
    fld_noload: "",
    dconsumption: "",
  });

  // Hour-basis UI (12h + AM/PM + dates)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startHour12, setStartHour12] = useState("12");
  const [startMinute, setStartMinute] = useState("00");
  const [startAmPm, setStartAmPm] = useState("AM");
  const [endHour12, setEndHour12] = useState("12");
  const [endMinute, setEndMinute] = useState("00");
  const [endAmPm, setEndAmPm] = useState("AM");
  const [endsNextDay, setEndsNextDay] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get("https://rfs2.infraconsoft.net.in/api/locations");
        setLocations(resp.data.locations || []);
      } catch {}
      await fetchVehiclePosition();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const ymdFromDateLikeLocal = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    return ymdLocal(d); // local YMD
  };

  const fetchVehiclePosition = async () => {
    try {
      const { data } = await axios.get(
        `https://rfs2.infraconsoft.net.in/api/vehicle_position/${id}`
      );
      const p = data?.position;
      if (!p) return;

      // Prefer precise start/end datetimes if present
      const sdPrecise = ymdFromDateLikeLocal(p.fld_start_dt);
      const edPrecise = ymdFromDateLikeLocal(p.fld_end_dt);
      // Fallback anchor = recorded date
      const anchor =
        ymdFromDateLikeLocal(p.fld_vdate) || ymdLocal(new Date());

      const st_dec = p.fld_sthrs ?? 0;
      const end_dec = p.fld_endhrs ?? 0;
      const wohrs = p.fld_wohrs ?? 0;

      const spanDays = edPrecise
        ? Math.max(
            0,
            Math.round(
              (new Date(edPrecise + "T00:00:00") -
                new Date((sdPrecise || anchor) + "T00:00:00")) / 86400000
            )
          )
        : daySpanFromStored(st_dec, end_dec, wohrs);

      const [sh, sm] = decToHM(st_dec);
      const [eh, em] = decToHM(end_dec);
      const s12 = h24To12(sh);
      const e12 = h24To12(eh);

      setFormData({
        fld_drivername: p.fld_drivername || "",
        fld_location: p.fld_location || "",
        fld_sthrs: st_dec,
        fld_endhrs: end_dec,
        fld_wohrs: wohrs,
        fld_stkm: p.fld_stkm ?? "",
        fld_endkm: p.fld_endkm ?? "",
        fld_vtype: p.fld_vtype || "",
        fld_vbasis: p.fld_vbasis || "",
        fld_vdate: p.fld_vdate || "",
        fld_load_date: p.fld_load_date || "",
        fld_unload_date: p.fld_unload_date || "",
        fld_dieselqty: p.fld_dieselqty ?? "",
        fld_noload: p.fld_noload ?? "",
        dconsumption: p.dconsumption ?? "",
      });

      const sd = sdPrecise || anchor;
      const ed = edPrecise || addDaysLocal(sd, spanDays);

      setStartDate(sd);
      setEndDate(ed);
      setEndsNextDay(spanDays === 1);

      setStartHour12(s12.h12);
      setStartMinute(pad2(sm));
      setStartAmPm(s12.ampm);
      setEndHour12(e12.h12);
      setEndMinute(pad2(em));
      setEndAmPm(e12.ampm);

      setLoaded(true);
    } catch (err) {
      console.error("Error fetching vehicle position details:", err);
    }
  };

  // Toggle next day (use local YMD)
  const onToggleEndsNextDay = (checked) => {
    setEndsNextDay(checked);
    if (!startDate) return;
    setEndDate(checked ? addDaysLocal(startDate, 1) : startDate);
  };
  const onChangeStartDate = (val) => {
    setStartDate(val);
    if (endsNextDay && val) setEndDate(addDaysLocal(val, 1));
  };

  // derived hour preview/duration
  const hStart24 = to24h(startHour12, startAmPm);
  const mStart = Number(startMinute);
  const hEnd24 = to24h(endHour12, endAmPm);
  const mEnd = Number(endMinute);
  const startMinutes = startDate
    ? minutesFromDateHM(startDate, hStart24, mStart)
    : NaN;
  const endMinutes = endDate
    ? minutesFromDateHM(endDate, hEnd24, mEnd)
    : NaN;
  const durationMinutes =
    isNaN(startMinutes) || isNaN(endMinutes)
      ? 0
      : Math.max(0, endMinutes - startMinutes);
  const durationHoursDec = +(durationMinutes / 60).toFixed(2);
  const startDec = decFromHM(hStart24, mStart);
  const endDec = decFromHM(hEnd24, mEnd);

  const previewWarnings = useMemo(() => {
    const msgs = [];
    if (startDate && endDate && startDate === endDate && endMinutes < startMinutes)
      msgs.push("End time is earlier than start time on the same day. Did you mean “Ends next day”?");
    if (durationMinutes === 0 && formData.fld_vbasis === "Hour")
      msgs.push("Duration is 0 minutes. If this is an overnight (e.g., 12 AM → next-day 12 AM), turn on “Ends next day”.");
    return msgs;
  }, [startDate, endDate, startMinutes, endMinutes, durationMinutes, formData.fld_vbasis]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isNonNegativeNumber = (v) =>
    v !== "" && !isNaN(Number(v)) && Number(v) >= 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fld_drivername?.trim())
      return Swal.fire("Validation Error", "Please enter a driver name.", "warning");
    if (!formData.fld_location?.trim())
      return Swal.fire("Validation Error", "Please select a location.", "warning");
    if (!formData.fld_load_date)
      return Swal.fire("Validation Error", "Please choose the loading date.", "warning");
    if (!formData.fld_unload_date)
      return Swal.fire("Validation Error", "Please choose the unloading date.", "warning");
    if (
      new Date(formData.fld_unload_date + "T00:00:00") <
      new Date(formData.fld_load_date + "T00:00:00")
    )
      return Swal.fire(
        "Validation Error",
        "Unloading date cannot be earlier than loading date.",
        "warning"
      );

    // ==== Build payload with API-expected keys (fld_*) ====
    let payload = {
      vcode,
      branch,
      vehno,
      vehname,
      vehicle_type: formData.fld_vtype,
      vehicle_basis: formData.fld_vbasis,
      driver: formData.fld_drivername,
      location: formData.fld_location,

      // IMPORTANT: use fld_* names so the server updates correctly
      fld_load_date: formData.fld_load_date
        ? ymdFromDateLikeLocal(formData.fld_load_date)
        : null,
      fld_unload_date: formData.fld_unload_date
        ? ymdFromDateLikeLocal(formData.fld_unload_date)
        : null,
      fld_dieselqty:
        formData.fld_dieselqty === "" ? null : Number(formData.fld_dieselqty),
      fld_noload:
        formData.fld_noload === "" ? null : Number(formData.fld_noload),
    };

    if (formData.fld_vbasis === "Km") {
      if (!formData.fld_stkm || !formData.fld_endkm)
        return Swal.fire("Validation Error", "Starting and Ending KM are required.", "warning");
      if (!isNonNegativeNumber(formData.fld_stkm) || !isNonNegativeNumber(formData.fld_endkm))
        return Swal.fire("Validation Error", "KM values must be non-negative numbers.", "warning");
      const startKm = Number(formData.fld_stkm);
      const endKm = Number(formData.fld_endkm);
      if (endKm < startKm)
        return Swal.fire("Validation Error", "Ending KM cannot be less than Starting KM.", "warning");

      payload = {
        ...payload,
        stkm: startKm,
        endkm: endKm,
        km_worked: endKm - startKm,
        sthrs: 0,
        endhrs: 0,
        work_hrs: 0,
      };
    } else {
      if (!startDate || !endDate)
        return Swal.fire("Validation Error", "Start and End dates are required.", "warning");
      if (startDate === endDate && endMinutes < startMinutes)
        return Swal.fire("Validation Error", "End time is earlier than start time. Enable “Ends next day”.", "warning");
      if (durationMinutes <= 0)
        return Swal.fire("Validation Error", "Duration is 0. Check your times or toggle “Ends next day”.", "warning");
      if (durationMinutes > 72 * 60)
        return Swal.fire("Validation Error", "Duration exceeds 72 hours. Please verify.", "warning");

      payload = {
        ...payload,
        sthrs: startDec,
        endhrs: endDec,
        work_hrs: durationHoursDec,
        hour_basis: {
          startDate,
          endDate,
          startHour12,
          startMinute,
          startAmPm,
          endHour12,
          endMinute,
          endAmPm,
        },
      };
    }

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const resp = await axios.put(
        `https://rfs2.infraconsoft.net.in/api/update_vehicle_position/${id}`,
        payload
      );
      if (resp.data?.success) {
        Swal.fire({
          title: "Success!",
          text: "Vehicle position updated successfully.",
          icon: "success",
        }).then(() =>
          navigate(
            `/vehicle_working_hours_own/${vcode}/${vehno}/${vehname}/${branch}/${formData.fld_vbasis}`
          )
        );
      } else {
        Swal.fire("Error", resp.data?.error || "Update failed.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Update Failed", "An unexpected error occurred.", "error");
    }
  };

  if (!canEdit) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => pad2(i));

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Update Vehicle Position</h2>
            <Link
              to={`/vehicle_working_hours_own/${vcode}/${vehno}/${vehname}/${branch}/${formData.fld_vbasis}`}
            >
              <p className="back-button">
                <i className="fa-solid fa-backward"></i>Back
              </p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="form-container">
          <form className="vehicle-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Code :</label>
                <input readOnly value={vcode} />
              </div>
              <div className="form-group">
                <label>Branch Name :</label>
                <input readOnly value={branch} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Name :</label>
                <input readOnly value={vehname} />
              </div>
              <div className="form-group">
                <label>Vehicle Type :</label>
                <input readOnly value={formData.fld_vtype} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Basis :</label>
                <input readOnly value={formData.fld_vbasis} />
              </div>
              <div className="form-group">
                <label>Driver Name :</label>
                <input
                  name="fld_drivername"
                  value={formData.fld_drivername}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <h2 className="center-">Enter Load Details</h2>

            {formData.fld_vbasis === "Hour" && (
              <>
                {/** Optional warning + toggle block */}
                {/** Keep even when no warnings if you want the toggle always visible */}
                {previewWarnings.length > 0 && (
                  <div className="alert alert--warn" role="status" aria-live="polite">
                    <div className="alert__icon" aria-hidden="true">⚠️</div>
                    <div className="alert__body">
                      <div className="alert__head">
                        <div className="alert__title">Check your time range</div>
                        <div className="toggle">
                          <input
                            id="endsNextDay"
                            type="checkbox"
                            checked={endsNextDay}
                            onChange={(e) => onToggleEndsNextDay(e.target.checked)}
                          />
                          <label htmlFor="endsNextDay">Ends next day</label>
                        </div>
                      </div>
                      <div className="alert__msg">
                        {previewWarnings.map((w, i) => (
                          <p key={i} className="warn">{w}</p>
                        ))}
                        <div className="alert__hint">
                          Overnight example: <b>12:00 AM → next-day 12:00 AM</b>.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <br />
              </>
            )}

            {formData.fld_vbasis === "Km" ? (
              <div className="form-row">
                <div className="form-group">
                  <label>Starting km :</label>
                  <input
                    name="fld_stkm"
                    value={formData.fld_stkm}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Closing km :</label>
                  <input
                    name="fld_endkm"
                    value={formData.fld_endkm}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date :</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => onChangeStartDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>End Date :</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Time :</label>
                    <div className="time-group field-control">
                      <select value={startHour12} onChange={(e) => setStartHour12(e.target.value)}>
                        {hourOptions.map((h) => <option key={h}>{h}</option>)}
                      </select>
                      <span className="sep">:</span>
                      <select value={startMinute} onChange={(e) => setStartMinute(e.target.value)}>
                        {minuteOptions.map((m) => <option key={m}>{m}</option>)}
                      </select>
                      <span className="sep"> </span>
                      <select value={startAmPm} onChange={(e) => setStartAmPm(e.target.value)}>
                        <option>AM</option><option>PM</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>End Time :</label>
                    <div className="time-group field-control">
                      <select value={endHour12} onChange={(e) => setEndHour12(e.target.value)}>
                        {hourOptions.map((h) => <option key={h}>{h}</option>)}
                      </select>
                      <span className="sep">:</span>
                      <select value={endMinute} onChange={(e) => setEndMinute(e.target.value)}>
                        {minuteOptions.map((m) => <option key={m}>{m}</option>)}
                      </select>
                      <span className="sep"> </span>
                      <select value={endAmPm} onChange={(e) => setEndAmPm(e.target.value)}>
                        <option>AM</option><option>PM</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* <div className="form-row">
                  <div className="form-group">
                    <label>Preview (24h) :</label>
                    <input
                      type="text"
                      readOnly
                      value={`Start ${formatDMY(startDate)} → End ${formatDMY(endDate)}${
                        endDate !== startDate ? " (crosses day)" : ""
                      }`}
                    />
                  </div>
                  <div className="form-group">
                    <label>Working Hours :</label>
                    <input type="text" readOnly value={`${durationHoursDec.toFixed(2)} hours`} />
                  </div>
                </div> */}
              </>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Date of Loading :</label>
                <input
                  type="date"
                  name="fld_load_date"
                  value={formData.fld_load_date ? ymdFromDateLikeLocal(formData.fld_load_date) : ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Date of UnLoading :</label>
                <input
                  type="date"
                  name="fld_unload_date"
                  value={formData.fld_unload_date ? ymdFromDateLikeLocal(formData.fld_unload_date) : ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Location :</label>
                <select
                  name="fld_location"
                  value={formData.fld_location}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">-- Select Location --</option>
                  {locations.map((l, i) => (
                    <option key={i} value={l.fld_location}>{l.fld_location}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>No of Loads :</label>
                <input
                  type="number"
                  name="fld_noload"
                  min="0"
                  step="1"
                  value={formData.fld_noload}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Diesel Qty :</label>
                <input
                  type="number"
                  name="fld_dieselqty"
                  min="0"
                  step="any"
                  value={formData.fld_dieselqty}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label></label>
                <input
                  type="hidden"
                  name="dconsumption"
                  min="0"
                  step="any"
                  value={formData.dconsumption}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="btn-cls">
              <button type="submit" className="submit-btn">Update</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VehiclePositionEdit;
